import type { StreamEvent } from '../types/chat.types';
import { getIdToken } from './authService';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-js';

export interface StreamingCallbacks {
  onToken?: (content: string) => void;
  onToolStart?: (tool: string, args: Record<string, any>) => void;
  onToolResult?: (tool: string, result: string) => void;
  onProgress?: (message: string, phase?: string) => void;
  onMetadata?: (data: any) => void;
  onWarning?: (message: string) => void;
  onDone?: (fullText: string, responseId: string) => void;
  onError?: (message: string) => void;
}

/**
 * Stream responses from the streaming endpoint
 * Handles SSE format: single-line JSON data frames
 */
export async function streamResponse(
  streamingEndpoint: string,
  payload: any,
  callbacks: StreamingCallbacks,
  abortSignal?: AbortSignal
): Promise<void> {
  // Get JWT token for Lambda-level auth (defence in depth)
  const token = await getIdToken();

  // Get Identity Pool AWS credentials for SigV4 signing
  const session = await fetchAuthSession();
  const credentials = session.credentials;
  if (!credentials) {
    throw new Error('No AWS credentials from Identity Pool — is REACT_APP_IDENTITY_POOL_ID configured?');
  }

  const url = new URL(streamingEndpoint);
  const bodyStr = JSON.stringify(payload);

  // Derive region from the Function URL hostname (e.g. "…lambda-url.us-west-2.on.aws")
  const regionPart = url.hostname.split('.')[2];
  if (!regionPart) {
    throw new Error(`Cannot derive AWS region from streaming endpoint hostname: ${url.hostname}`);
  }

  const signer = new SignatureV4({
    credentials,
    region: regionPart,
    service: 'lambda',
    sha256: Sha256,
  });

  // Build a proper HttpRequest for SigV4 signing.
  // The signer computes the real SHA-256 body hash (UNSIGNED-PAYLOAD
  // is not supported by Lambda Function URLs).
  const request = new HttpRequest({
    method: 'POST',
    protocol: 'https:',
    hostname: url.hostname,
    port: 443,
    path: url.pathname || '/',
    headers: {
      host: url.hostname,
      'content-type': 'application/json',
    },
    body: bodyStr,
  });

  const signedRequest = await signer.sign(request);

  // Add JWT AFTER signing — not part of SigV4 canonical request
  const hdrs: Record<string, string> = { ...(signedRequest.headers as Record<string, string>) };
  hdrs['x-chat-authorization'] = `Bearer ${token}`;

  const response = await fetch(streamingEndpoint, {
    method: 'POST',
    headers: hdrs,
    body: bodyStr,
    signal: abortSignal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[Streaming] Error response:', response.status, errorText);
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentDataLines: string[] = []; // For multiline data: frames
  let streamTerminated = false; // Track if we received a 'done' or 'error' event

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines (handle both \n and \r\n)
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        // Empty line marks end of SSE event (per SSE spec)
        if (!line.trim()) {
          // Process accumulated multiline data if any
          if (currentDataLines.length > 0) {
            if (processDataEvent(currentDataLines.join('\n'), callbacks)) {
              streamTerminated = true;
            }
            currentDataLines = [];
          }
          continue;
        }

        // Skip SSE comments (heartbeats)
        if (line.startsWith(':')) {
          continue;
        }

        // Handle event: field (optional - currently not used by backend)
        if (line.startsWith('event:')) {
          // Could store event type for future use
          continue;
        }

        // Handle id: field (optional - currently not used by backend)
        if (line.startsWith('id:')) {
          // Could store event ID for reconnection
          continue;
        }

        // Accumulate data: lines (handles multiline per SSE spec)
        if (line.startsWith('data:')) {
          const dataContent = line.startsWith('data: ')
            ? line.slice(6)
            : line.slice(5);
          currentDataLines.push(dataContent);
        }
      }
    }

    // Process any remaining data at end of stream
    if (currentDataLines.length > 0) {
      if (processDataEvent(currentDataLines.join('\n'), callbacks)) {
        streamTerminated = true;
      }
    }

    // If stream ended without a 'done' or 'error' event, the backend likely crashed
    if (!streamTerminated && callbacks.onError) {
      console.error('[Streaming] Stream ended unexpectedly without done event');
      callbacks.onError('stream_disconnected');
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Process a complete SSE data event
 * @returns true if this was a terminal event ('done' or 'error')
 */
function processDataEvent(data: string, callbacks: StreamingCallbacks): boolean {
  try {
    const event: StreamEvent = JSON.parse(data);

    switch (event.type) {
      case 'connected':
        if (event.message && callbacks.onProgress) {
          callbacks.onProgress(event.message, 'connected');
        }
        return false;

      case 'progress':
        if (event.message && callbacks.onProgress) {
          callbacks.onProgress(event.message, event.phase);
        }
        return false;

      case 'token':
        if (event.content && callbacks.onToken) {
          callbacks.onToken(event.content);
        }
        return false;

      case 'tool_call_start':
        if (event.tool && callbacks.onToolStart) {
          callbacks.onToolStart(event.tool, event.args || {});
        }
        return false;

      case 'tool_call_result':
        if (event.tool && event.result && callbacks.onToolResult) {
          callbacks.onToolResult(event.tool, event.result);
        }
        return false;

      case 'metadata':
        if (callbacks.onMetadata) {
          callbacks.onMetadata(event.data);
        }
        return false;

      case 'warning':
        if (event.message && callbacks.onWarning) {
          callbacks.onWarning(event.message);
        }
        return false;

      case 'done':
        if (event.fullText && event.responseId && callbacks.onDone) {
          callbacks.onDone(event.fullText, event.responseId);
        }
        return true;

      case 'error':
        if (event.message && callbacks.onError) {
          callbacks.onError(event.message);
        }
        return true;

      default:
        console.warn('Unknown event type:', event.type);
        return false;
    }
  } catch (parseError) {
    console.error('Failed to parse SSE event:', parseError, 'Data:', data);
    return false;
  }
}

/**
 * Cancel streaming (for future implementation)
 */
export function cancelStream(reader: ReadableStreamDefaultReader<Uint8Array>) {
  reader.cancel();
}
